import React, { useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Alert, Badge, Spinner } from 'react-bootstrap';
import { ColumnDescription } from 'react-bootstrap-table-next';
import PageTitle from '../headers/PageTitle';
import StylishNewTable from '../../components/DesignSystems/New/StylishNewTable';
import { useAlertConfigById } from './hooks/useAlertConfigs';
import { useAlertEventInstanceById } from './hooks/useAlertEventInstances';
import { useAlertRecipients } from './hooks/useAlertRecipients';
import { useApplyAcknowledgement } from './hooks/useAlertAcknowledgements';
import { AcknowledgeButton } from './components/AcknowledgeButton';
import { AcknowledgmentSummary } from './components/AcknowledgmentSummary';

interface AlertRecipient {
  userGuid: string;
  userName: string;
  email: string;
  acknowledgmentRequired: boolean;
  acknowledgedAt: string | null;
  acknowledgmentId: string | null;
}

const AlertRecipientsTable: React.FC = () => {
  const navigate = useNavigate();
  const { alert_config_id: alertConfigId, report_id: reportId } = useParams();

  const { data: config, isLoading: isLoadingConfig } = useAlertConfigById(
    alertConfigId || ''
  );
  const { data: eventInstance, isLoading: isLoadingInstance } =
    useAlertEventInstanceById(reportId || '');
  const { data: recipients = [], isLoading: isLoadingRecipients } =
    useAlertRecipients(reportId || '');
  const applyAcknowledgementMutation = useApplyAcknowledgement();

  const [page, setPage] = useState<number>(1);
  const perPage = 10;

  // Memoize recipients summary - must be called before any early returns
  const recipientsSummary = useMemo(
    () =>
      recipients.map((recipient) => ({
        id: recipient.acknowledgmentId || recipient.userGuid,
        userGuid: recipient.userGuid,
        acknowledgedAt: recipient.acknowledgedAt,
        acknowledgmentRequired: recipient.acknowledgmentRequired,
      })),
    [recipients]
  );

  // Combine loading states
  const isLoading = isLoadingConfig || isLoadingInstance || isLoadingRecipients;

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <Spinner animation="border" />
      </div>
    );
  }

  if (!config || !eventInstance) {
    return <div>Alert configuration or report not found</div>;
  }

  const recipientCount = recipients?.length || 0;
  const hasRecipients = recipientCount > 0;

  const handleAcknowledge = (recipient: AlertRecipient) => {
    if (recipient.acknowledgmentId) {
      applyAcknowledgementMutation.mutate({
        acknowledgementId: recipient.acknowledgmentId,
      });
    }
  };

  const columns: ColumnDescription[] = [
    {
      dataField: 'userName',
      text: 'User Name',
      sort: true,
    },
    {
      dataField: 'email',
      text: 'Email',
      sort: true,
    },
    {
      dataField: 'acknowledgmentRequired',
      text: 'Acknowledgment Required',
      sort: true,
      formatter: (cell) => (
        <Badge bg={cell ? 'warning' : 'secondary'}>{cell ? 'Yes' : 'No'}</Badge>
      ),
    },
    {
      dataField: 'acknowledgedAt',
      text: 'Acknowledgment Status',
      sort: true,
      formatter: (cell, row) => {
        if (!row?.acknowledgmentRequired) {
          return <Badge bg="secondary">Not Required</Badge>;
        }

        if (cell) {
          return (
            <div className="d-flex flex-column">
              <Badge bg="success" className="mb-1">
                ✓ Acknowledged
              </Badge>
              <small className="text-success fw-medium">
                {new Date(cell).toLocaleString()}
              </small>
            </div>
          );
        }

        return <Badge bg="warning">Pending</Badge>;
      },
    },
    {
      dataField: 'actions',
      text: 'Actions',
      sort: false,
      formatter: (cell, row) => {
        if (!row?.acknowledgmentRequired) {
          return null;
        }

        if (row.acknowledgedAt) {
          return (
            <div className="d-flex flex-column align-items-center">
              <Badge bg="success" className="mb-1">
                ✓ Acknowledged
              </Badge>
              <small className="text-success fw-medium">
                {new Date(row.acknowledgedAt).toLocaleString()}
              </small>
            </div>
          );
        }

        return (
          <AcknowledgeButton
            onClick={() => handleAcknowledge(row)}
            isLoading={applyAcknowledgementMutation.isPending}
            disabled={applyAcknowledgementMutation.isPending}
            variant="warning"
            size="sm"
            showIcon={true}
          />
        );
      },
    },
  ];

  return (
    <div className="p-4 pt-0">
      <PageTitle
        title={`Recipients - ${eventInstance.name}`}
        parents={[
          'Alert Configurations',
          config?.name || 'Loading...',
          'Reports',
        ]}
        onBack={() => navigate(`/aialerts/${alertConfigId}/reports`)}
      />

      <p className="text-muted mb-4">
        This shows all users who received this alert and their acknowledgment
        status.
      </p>

      {/* Summary Alert */}
      <AcknowledgmentSummary
        acknowledgements={recipientsSummary}
        variant="info"
        showLastAcknowledgment={true}
      />

      <div className="bg-neutral-800 rounded-1 p-3">
        {!hasRecipients ? (
          <Alert variant="info" className="mt-3">
            <strong>No Recipients Found</strong>
            <p className="mb-0">
              No recipients were found for this alert report. This may indicate
              that the alert did not require notifications or acknowledgments.
            </p>
          </Alert>
        ) : (
          <StylishNewTable
            keyField="userGuid"
            columns={columns}
            rows={recipients}
            pagination
            page={page}
            perPage={perPage}
            total={recipients.length}
            onPageChange={setPage}
            noDataIndication="No recipients found"
          />
        )}
      </div>
    </div>
  );
};

export default AlertRecipientsTable;
